/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 01-Aug-2026
 * @author      : Vijayakumar S, with Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Communication Management §9 — token picker (C4 catalog)
 * Purpose      : Renders the C4 token catalog, grouped, and inserts a placeholder.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * §9             | 01-Aug-2026 | Vijayakumar S  | Initial creation
 * Comms copy     | 06-Aug-2026 | Claude         | Removed the "only tokens a send-time resolver
 * removed                                         actually populates" caption, at the user's
 *                                                 request.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { placeholderFor, tokensByGroup } from './CommunicationTokenCatalog';

/**
 * Renders exactly what the C4 catalog contains — nothing more.
 *
 * §9's worked example uses `{{customer.firstName}}`. That token is NOT offered here and
 * was not added to make the example work: `gmscloud-customer-data` is not on
 * provider-api's classpath, so `customer.*` cannot resolve in this app, and the live
 * namespace is `client.*` regardless. Offering it would produce templates that save
 * cleanly and then render blank for every tenant at send time.
 *
 * The catalog is sectioned by `TokenGroup`; the picker follows that sectioning rather
 * than inventing its own, so a group added server-side appears here automatically.
 */
export interface TokenPickerProps {
  /** Called with the full `{{token.key}}` placeholder to insert at the caret. */
  onInsert: (placeholder: string) => void;
  disabled?: boolean;
  /**
   * The template's `template_category`, so the picker can withhold tokens that only one flow can
   * resolve. Empty/undefined means "not classified yet", which is the normal state for a brand-new
   * draft (a category is assigned separately, after creation) — those see the unrestricted list.
   */
  templateCategory?: string | null;
}

/**
 * Groups whose tokens ONLY one flow can bind, keyed by the categories allowed to use them.
 *
 * <p>CONTRACT exists because `contract.link` is the catalogue's one caller-supplied token: it is
 * minted per send from an encrypted payload by the contract sender and has NO resolver. Every other
 * token comes from `resolveAutomationTokens`, which runs for every flow. So a template outside the
 * contract flow that carries `{{contract.link}}` passes authoring validation — the token IS
 * catalogued — and then throws at send, because `SmsTemplateRenderer.render` refuses any unbound
 * placeholder. That renderer is shared by automation dispatch, estimate SMS, invoice SMS,
 * task-action email and tenant SMS, so the blast radius is every channel, not just contract email.
 *
 * <p>This is the CONVENIENCE half of the guard: it stops the picker offering a token that will
 * break the send. It is deliberately not the control — a direct API call still gets through. The
 * server-side half needs `requireKnownTokens` to become category-aware, which is a wider change
 * than it looks because a category is assigned AFTER creation (`setCategory`), so `createDraft` and
 * `proposeChange` do not yet know one. Tracked separately rather than half-built here.
 */
const CATEGORY_RESTRICTED_GROUPS: Record<string, readonly string[]> = {
  CONTRACT: ['CONTRACT_DRAFT', 'CONTRACT_SENT', 'CONTRACT_CONFIRMED'],
};

export const TokenPicker: React.FC<TokenPickerProps> = ({
  onInsert, disabled = false, templateCategory,
}) => {
  const groups = tokensByGroup().filter((g) => {
    const allowed = CATEGORY_RESTRICTED_GROUPS[g.group];
    if (!allowed) return true;
    return templateCategory != null && allowed.includes(templateCategory);
  });

  return (
    <Paper variant="outlined" data-testid="token-picker" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Insert a token
      </Typography>
      <Divider sx={{ mb: 1.5 }} />

      {groups.map((group) => (
        <Box key={group.group} sx={{ mb: 1.5 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.6 }}
          >
            {group.label}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {group.tokens.map((token) => (
              <Tooltip key={token.tokenKey} title={placeholderFor(token.tokenKey)} arrow>
                <span>
                  <Chip
                    label={token.label}
                    size="small"
                    variant="outlined"
                    clickable={!disabled}
                    disabled={disabled}
                    onClick={() => onInsert(placeholderFor(token.tokenKey))}
                    data-testid={`token-chip-${token.tokenKey}`}
                  />
                </span>
              </Tooltip>
            ))}
          </Box>
        </Box>
      ))}
    </Paper>
  );
};

export default TokenPicker;
